import { useContext } from 'react';
import { ContextParameter } from '@lmig/dotcom-aspect-components/ContextParameter';

/**
 * Provides utilities for appending parameters to the end of URLs. Query parameters
 * appended to URLs on page load (or route change) are stored in a `ContextParameter`
 * and this hook is used to extract and apply those parameters.
 *
 * @example
 * import useLinkAppend from '../hooks/useLinkAppend';
 *
 * const { appendParams } = useLinkAppend();
 * const link = appendParams('/login');
 * console.log(link) // /login?MM_webID=23423
 */
const useLinkAppend = () => {
  const contextValues = useContext(ContextParameter);

  if (!contextValues) {
    throw new Error('useLinkAppend needs to be within a <ParameterContextProvider>');
  }

  const { values: parameterContextValues = {} } = contextValues;

  let paramsToExpose = { ...parameterContextValues };

  if (
    'r_webID' in paramsToExpose &&
    (!('MM_webID' in paramsToExpose) || paramsToExpose.r_webID === paramsToExpose.MM_webID)
  ) {
    // eslint-disable-next-line camelcase
    const { r_webID: rWebId, ...rest } = paramsToExpose;

    paramsToExpose = {
      MM_webID: rWebId,
      ...rest,
    };
  }

  return {
    /**
     * Object containing all keys/values currently in context
     * @type {object}
     */
    parameters: { ...paramsToExpose },

    /**
     * Appends all parameters stored in `ContextParameter` to a URL/URI.
     *
     * @param {string} baseUrl The URL to append query parameters to
     * @param {object} [defaultValues] An object containing key/value pairs to also append
     * @param {object} [options]
     * @param {boolean} [options.removeEmpty] Whether empty query parameters are output
     * @returns {string}
     * @example
     * import useLinkAppend from '../hooks/useLinkAppend';
     *
     * const { appendParams } = useLinkAppend();
     * <Link href={appendParams('/login/', { mode: 'iframe' })} />
     */

    appendParams: (baseUrl = '', defaultValues = {}, options = {}) => {
      const phonyBase = 'https://trim.trim';
      const url = new URL(baseUrl, phonyBase);

      const { isQuoting = true } = options;

      if (isQuoting) {
        delete paramsToExpose.zip;
        delete paramsToExpose.zipcode;
        delete paramsToExpose.zipCode;
        paramsToExpose.zipCode = defaultValues.zipCode;
        paramsToExpose.jurisdictionPage = defaultValues.jurisdictionPage;
      }

      const paramsForAppending = { ...defaultValues, ...paramsToExpose };
      const { removeEmpty = true } = options;

      let toAppend;

      if (removeEmpty) {
        toAppend = Object.entries(paramsForAppending).reduce((acc, [key, val]) => {
          if (!val) {
            return acc;
          }

          return {
            ...acc,
            [key]: val,
          };
        }, {});
      } else {
        toAppend = paramsForAppending;
      }

      Object.entries(toAppend).forEach(([key, val]) => {
        url.searchParams.append(key, val);
      });
      const urlString = url.toString();

      return urlString.startsWith(phonyBase) ? urlString.substring(phonyBase.length) : urlString;
    },
  };
};

export default useLinkAppend;
